import { RootState } from 'app/store';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { PaginationRequest } from '../types/paginationRequest';
import moment from 'moment';
import { PaginationResponse } from '../types/paginationResponse';
import {
  initializePaginationOptions,
  selectLastPaginationRequest,
  selectNullablePaginationOptions,
  setPreviousRequest
} from '../paginationOptionsSlice';
import { PrefetchOptions } from '@reduxjs/toolkit/dist/query/core/module';
import {
  TypedUseQueryStateResult,
  UseQuery
} from '@reduxjs/toolkit/dist/query/react/buildHooks';

export interface PaginationInitialOptions {
  initialColumnName: string;
  initialOrderDirection: string;
}

const DEFAULT_PAGE_SIZE = 10;

export function usePaginatedQuery<RequestType, ResponseType>(
  paginatedQueryCallback: any,
  prefetchPageFn: (
    arg: PaginationRequest<RequestType>,
    options?: PrefetchOptions | undefined
  ) => void,
  componentId: string,
  initialRequestData: RequestType,
  initialRequestOptions: PaginationInitialOptions
) {
  const isFirstRequest = useRef(true);
  const dispatch = useDispatch();
  const { siteId } = useSelector((state: RootState) => state.selectedSite);
  const lastPaginationRequest = useSelector((state: RootState) =>
    selectLastPaginationRequest(state, componentId)
  );
  const paginationOptions = useSelector((state: RootState) =>
    selectNullablePaginationOptions(state, componentId)
  );

  if (!paginationOptions) {
    dispatch(
      initializePaginationOptions({
        componentId,
        defaultColumnName: initialRequestOptions.initialColumnName,
        orderDirection: initialRequestOptions.initialOrderDirection
      })
    );
  }

  const { startDate, endDate } = useSelector(
    (state: RootState) => state.dateRange
  );

  const nextRequest = useMemo<PaginationRequest<RequestType>>(() => {
    let seekValue: string | undefined = paginationOptions?.seekValue;
    let seekId: string | undefined = paginationOptions?.seekId;
    let seekDirection: string | undefined = paginationOptions?.seekDirection;

    if (
      seekValue &&
      seekId &&
      seekDirection &&
      (!paginationOptions?.currentPage || paginationOptions?.currentPage === 1)
    ) {
      seekValue = undefined;
      seekId = undefined;
      seekDirection = undefined;
    }

    return {
      pageSize: paginationOptions?.pageSize ?? DEFAULT_PAGE_SIZE,
      page: paginationOptions?.currentPage ?? 1,
      startDate: moment(startDate).format('DD/MM/YYYY'),
      endDate: moment(endDate).format('DD/MM/YYYY'),
      requestDetails: { ...initialRequestData, siteId },
      searchTerm: paginationOptions?.searchTerm,
      seekValue,
      seekId,
      seekDirection,
      seekColumnName:
        paginationOptions?.seekColumnName ??
        initialRequestOptions.initialColumnName,
      orderDirection:
        paginationOptions?.orderDirection ??
        initialRequestOptions.initialOrderDirection
    };
  }, [
    paginationOptions?.seekValue,
    paginationOptions?.seekId,
    paginationOptions?.seekDirection,
    paginationOptions?.currentPage,
    paginationOptions?.pageSize,
    paginationOptions?.searchTerm,
    paginationOptions?.seekColumnName,
    paginationOptions?.orderDirection,
    startDate,
    endDate,
    initialRequestData,
    siteId,
    initialRequestOptions.initialColumnName,
    initialRequestOptions.initialOrderDirection
  ]);

  const value: TypedUseQueryStateResult<
    PaginationResponse<ResponseType>,
    any,
    any
  > = paginatedQueryCallback(nextRequest);

  const prefetchNext = useCallback(
    (request: PaginationRequest<RequestType>) => {
      prefetchPageFn(request);
    },
    [prefetchPageFn]
  );

  useEffect(() => {
    if (value.status === 'fulfilled') {
      const returnedPaginationInfo =
        value.data as PaginationResponse<ResponseType>;
      if (
        isFirstRequest.current ||
        (IsReturnedPaginationInfoDefined(returnedPaginationInfo) &&
          IsPreviousRequestDifferent(
            returnedPaginationInfo,
            lastPaginationRequest
          ))
      ) {
        isFirstRequest.current = false;

        if (returnedPaginationInfo.totalPages > 1) {
          prefetchNext({
            page: returnedPaginationInfo.page + 1,
            pageSize: returnedPaginationInfo.pageSize,
            startDate: moment(startDate).format('DD/MM/YYYY'),
            endDate: moment(endDate).format('DD/MM/YYYY'),
            requestDetails: { ...initialRequestData, siteId },
            searchTerm: paginationOptions?.searchTerm,
            seekValue: returnedPaginationInfo.forwardSeekValue,
            seekId: returnedPaginationInfo.forwardSeekId,
            seekDirection: 'pageForward',
            seekColumnName: returnedPaginationInfo.seekColumnName.toLowerCase(),
            orderDirection: returnedPaginationInfo.orderDirection
          });
        }

        if (returnedPaginationInfo.page > 1) {
          prefetchNext({
            page: returnedPaginationInfo.page - 1,
            pageSize: returnedPaginationInfo.pageSize,
            startDate: moment(startDate).format('DD/MM/YYYY'),
            endDate: moment(endDate).format('DD/MM/YYYY'),
            requestDetails: { ...initialRequestData, siteId },
            searchTerm: paginationOptions?.searchTerm,
            seekValue: returnedPaginationInfo.backwardSeekValue,
            seekId: returnedPaginationInfo.backwardSeekId,
            seekDirection: 'pageBackward',
            seekColumnName: returnedPaginationInfo.seekColumnName.toLowerCase(),
            orderDirection: returnedPaginationInfo.orderDirection
          });
        }

        dispatch(
          setPreviousRequest({
            componentId,
            previousRequest: {
              currentPage: returnedPaginationInfo.page,
              pageSize: returnedPaginationInfo.pageSize,
              totalPages: returnedPaginationInfo.totalPages,
              totalRecords: returnedPaginationInfo.totalRecords,
              forwardSeekValue: returnedPaginationInfo.forwardSeekValue,
              forwardSeekId: returnedPaginationInfo.forwardSeekId,
              backwardSeekValue: returnedPaginationInfo.backwardSeekValue,
              backwardSeekId: returnedPaginationInfo.backwardSeekId,
              orderDirection: returnedPaginationInfo.orderDirection,
              seekColumnName: returnedPaginationInfo.seekColumnName
            }
          })
        );
      }
    }
  }, [
    componentId,
    dispatch,
    endDate,
    initialRequestData,
    lastPaginationRequest,
    paginationOptions?.searchTerm,
    prefetchNext,
    siteId,
    startDate,
    value.data,
    value.status
  ]);

  return value;
}

function IsReturnedPaginationInfoDefined<ResponseType>({
  data,
  page,
  pageSize,
  totalPages,
  totalRecords,
  forwardSeekValue,
  forwardSeekId,
  backwardSeekValue,
  backwardSeekId,
  seekColumnName,
  orderDirection
}: PaginationResponse<ResponseType>) {
  return (
    page &&
    pageSize &&
    totalPages &&
    totalRecords &&
    forwardSeekValue &&
    forwardSeekId &&
    backwardSeekValue &&
    backwardSeekId &&
    seekColumnName &&
    orderDirection
  );
}

function IsPreviousRequestDifferent<ResponseType>(
  {
    page: newPage,
    pageSize: newPageSize,
    totalPages: newTotalPages,
    totalRecords: newTotalRecords,
    forwardSeekValue: newForwardSeekValue,
    forwardSeekId: newForwardSeekId,
    backwardSeekValue: newBackwardSeekValue,
    backwardSeekId: newBackwardSeekId,
    seekColumnName: newSeekColumnName,
    orderDirection: newOrderDirection
  }: PaginationResponse<ResponseType>,
  {
    currentPage: lastCurrentPage,
    pageSize: lastPageSize,
    totalPages: lastTotalPages,
    totalRecords: lastTotalRecords,
    forwardSeekValue: returnedForwardSeekValue,
    forwardSeekId: returnedForwardSeekId,
    backwardSeekValue: returnedBackwardSeekValue,
    backwardSeekId: returnedBackwardSeekId,
    seekColumnName: returnedSeekColumnName,
    orderDirection: returnedOrderDirection
  }: {
    currentPage?: number | undefined;
    pageSize?: number | undefined;
    totalPages?: number | undefined;
    totalRecords?: number | undefined;
    forwardSeekValue?: string | undefined;
    forwardSeekId?: string | undefined;
    backwardSeekValue?: string | undefined;
    backwardSeekId?: string | undefined;
    seekColumnName?: string | undefined;
    orderDirection?: string | undefined;
  }
): boolean {
  return !(
    newPage === lastCurrentPage &&
    newPageSize === lastPageSize &&
    newTotalPages === lastTotalPages &&
    newTotalRecords === lastTotalRecords &&
    newForwardSeekValue === returnedForwardSeekValue &&
    newForwardSeekId === returnedForwardSeekId &&
    newBackwardSeekValue === returnedBackwardSeekValue &&
    newBackwardSeekId === returnedBackwardSeekId &&
    newSeekColumnName === returnedSeekColumnName &&
    newOrderDirection === returnedOrderDirection
  );
}
